import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { AppManifest } from '@api/apps';
import { pendingAppStarts } from '@utils/pendingAppStarts';
import type { StartRequest } from './appFrame/useAppRunController';

interface StoredFile {
  readonly name: string;
  readonly type: string;
  readonly lastModified: number;
  readonly bytes: ArrayBuffer;
}

// Files are kept as plain bytes: not every browser's IndexedDB keeps a File or Blob intact.
interface StoredStart extends Omit<StartRequest, 'files'> {
  readonly files: readonly StoredFile[];
}

const pendingKey = (appKey: string) => ['apps', 'pendingStart', appKey] as const;

export const savePendingAppStart = async (appKey: string, request: StartRequest): Promise<void> => {
  const files = await Promise.all(
    request.files.map(async (file) => ({
      name: file.name,
      type: file.type,
      lastModified: file.lastModified,
      bytes: await file.arrayBuffer(),
    })),
  );
  await pendingAppStarts.save<StoredStart>(appKey, { ...request, files });
};

export const readPendingAppStart = async (appKey: string): Promise<StartRequest | null> => {
  const stored = await pendingAppStarts.peek<StoredStart>(appKey);
  if (!stored) return null;
  return {
    ...stored,
    files: stored.files.map((file) => new File([file.bytes], file.name, { type: file.type, lastModified: file.lastModified })),
  };
};

/** The page may have dropped options since the visitor picked them. */
export const withDeclaredOptions = (request: StartRequest, manifest: AppManifest): StartRequest => {
  const declared = new Set(manifest.options.map((option) => option.id));
  return {
    ...request,
    options: Object.fromEntries(Object.entries(request.options).filter(([id]) => declared.has(id))),
  };
};

/** The Run a visitor asked for before signing up, read without consuming it. */
export const usePendingAppStart = (appKey: string | undefined, enabled: boolean) => {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: pendingKey(appKey ?? 'none'),
    queryFn: () => readPendingAppStart(appKey as string).catch(() => null),
    enabled: Boolean(appKey) && enabled,
    staleTime: Infinity,
    retry: false,
  });
  const forget = useCallback(async () => {
    if (!appKey) return;
    await pendingAppStarts.remove(appKey).catch(() => undefined);
    queryClient.setQueryData(pendingKey(appKey), null);
  }, [appKey, queryClient]);
  return { pending: query.data ?? null, forget };
};
